import { useForm, useFieldArray } from "react-hook-form";
import { FaPlus, FaTrash } from "react-icons/fa";
import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import Loader from "../../pages/Loader";

const EditPlotModal = ({ plot, onClose, onUpdate }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const MAX_IMAGES = 10;

  useEffect(() => {
    if (plot?.photos) {
      setExistingImages(plot.photos);
    }
  }, [plot]);


  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...plot,
      distances: plot?.distanceFrom || [{ locality: "", distanceFrom: "" }],
      keyFeatures: plot?.keyFeatures?.map((feature) => ({
        value: feature,
      })) || [{ value: "" }],
    },
  });

  const {
    fields: distanceFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "distances",
  });


  const {
    fields: keyFeatureFields,
    append: appendKeyFeature,
    remove: removeKeyFeature,
  } = useFieldArray({
    control,
    name: "keyFeatures",
  });

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages =
      selectedImages.length +
      existingImages.length +
      files.length -
      imagesToDelete.length;

    if (totalImages > MAX_IMAGES) {
      Swal.fire({
        icon: "warning",
        title: "Image Limit Reached",
        text: `You can only upload a maximum of ${MAX_IMAGES} images.`,
        confirmButtonColor: "#f59e0b",
      });

      const allowedFiles = files.slice(
        0,
        MAX_IMAGES -
          (selectedImages.length +
            existingImages.length -
            imagesToDelete.length)
      );
      setSelectedImages((prev) => [...prev, ...allowedFiles]);
    } else {
      setSelectedImages((prev) => [...prev, ...files]);
    }
  };

  const handleRemoveNewImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index) => {
    const imageToRemove = existingImages[index];
    setImagesToDelete((prev) => [...prev, imageToRemove]);
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRestoreImage = (index) => {
    const imageToRestore = imagesToDelete[index];
    setExistingImages((prev) => [...prev, imageToRestore]);
    setImagesToDelete((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    try {
      const uploadedUrls = [];

      setLoading(true);
      // Upload new images to Cloudinary
      if (selectedImages.length > 0) {
        const uploadPromises = selectedImages.map((file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("upload_preset", "prayagproperties");

          return fetch(
            "https://api.cloudinary.com/v1_1/djmyigser/image/upload",
            {
              method: "POST",
              body: formData,
            }
          ).then((res) => res.json());
        });

        const cloudinaryResults = await Promise.all(uploadPromises);

        // Check for errors and extract URLs
        for (const result of cloudinaryResults) {
          if (!result.secure_url) {
            throw new Error("One or more uploads failed.");
          }
          uploadedUrls.push(result.secure_url);
        }
      }

      // Prepare the distances array with _id for existing entries
      const updatedDistances = data.distances.map((dist, index) => {
        if (
          plot.distances &&
          plot.distances[index] &&
          plot.distances[index]._id
        ) {
          return {
            locality: dist.locality,
            distanceFrom: dist.distanceFrom,
            _id: plot.distances[index]._id,
          };
        }
        return {
          locality: dist.locality,
          distanceFrom: dist.distanceFrom,
        };
      });

      // Combine existing (not deleted) images with new uploaded ones
      const allPhotos = [...existingImages, ...uploadedUrls];

      const payload = {
        ...data,
        _id: plot._id,
        photos: allPhotos, // This now contains all image URLs
        keyFeatures: data.keyFeatures.map((item) => item.value),
        distances: updatedDistances,
        imagesToDelete: imagesToDelete, // Array of image URLs to delete
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/update_plot/${plot._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json", // This is correct since you're sending JSON
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await res.json();

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Plot Updated",
          text: responseData.message || "The Plot was updated successfully!",
          confirmButtonColor: "#2563eb",
        });
        onUpdate(responseData.plot);
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            responseData.message || "Something went wrong while updating plot.",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      console.error("Plot update error:", error);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to update plot. Please try again later.",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { label: "Title", name: "title", type: "text", required: true },
    { label: "Address", name: "address", type: "text", required: true },
    {
      label: "Description",
      name: "description",
      type: "textarea",
      required: true,
    },
    { label: "Price", name: "price", type: "number", required: true },
    { label: "Size (sqft)", name: "size", type: "text", required: true },
    {
      label: "Availability",
      name: "availability",
      type: "select",
      options: ["Yes", "No"],
      required: true,
    },
    // { label: "Approved By", name: "approved", type: "text", required: true },
    { label: "Contact No", name: "contactNo", type: "text", required: true },
    { label: "Seller Name", name: "sellerName", type: "text", required: true },
    { label: "Soil Type", name: "soilType", type: "text", required: true },
    { label: "Facing Direction", name: "facing", type: "text", required: true },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-start py-10 overflow-y-auto">
      <motion.div
        className="w-full max-w-5xl bg-white rounded-xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="bg-gradient-to-r from-blue-700 to-cyan-500 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Edit Plot</h2>
          <button
            onClick={onClose}
            className="text-white text-lg hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6 md:p-10 grid gap-6 grid-cols-1 md:grid-cols-2 bg-gray-50"
        >
          {fields.map((field) => (
            <div key={field.name}>
              <label className="block mb-3 text-sm font-bold text-gray-700">
                {field.label}{" "}
                {field.required && <span className="text-red-500">*</span>}
              </label>

              {field.type === "textarea" ? (
                <textarea
                  {...register(field.name, {
                    required: field.required
                      ? `${field.label} is required`
                      : false,
                  })}
                  rows={3}
                  className={`w-full resize-none border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                    errors[field.name]
                      ? "border-red-500 focus:ring-red-400"
                      : "border-gray-300 focus:ring-blue-400"
                  }`}
                />
              ) : field.type === "select" ? (
                <select
                  {...register(field.name, {
                    required: field.required
                      ? `${field.label} is required`
                      : false,
                  })}
                  className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                    errors[field.name]
                      ? "border-red-500 focus:ring-red-400"
                      : "border-gray-300 focus:ring-blue-400"
                  }`}
                >
                  <option value="" disabled>
                    Select {field.label}
                  </option>
                  {field.options.map((opt) => (
                    <option
                      key={opt}
                      value={opt}
                      selected={plot[field.name] === opt}
                    >
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  {...register(field.name, {
                    required: field.required
                      ? `${field.label} is required`
                      : false,
                  })}
                  className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                    errors[field.name]
                      ? "border-red-500 focus:ring-red-400"
                      : "border-gray-300 focus:ring-blue-400"
                  }`}
                />
              )}

              {errors[field.name] && (
                <p className="text-sm text-red-600 mt-1">
                  {errors[field.name]?.message}
                </p>
              )}
            </div>
          ))}

          {/* Key Features Fields */}
          <div className="col-span-1">
            <label className="block font-semibold text-md text-gray-800 mb-2">
              Key Features <span className="text-red-500">*</span>
            </label>
            {keyFeatureFields.map((item, index) => (
              <div key={item.id} className="relative mb-4">
                <input
                  placeholder="Enter Key Feature"
                  {...register(`keyFeatures.${index}.value`, {
                    required: "Key feature is required",
                  })}
                  className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                    errors?.keyFeatures?.[index]?.value
                      ? "border-red-500 focus:ring-red-400"
                      : "border-gray-300 focus:ring-blue-400"
                  }`}
                />

                {keyFeatureFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeKeyFeature(index)}
                    className="absolute right-3 top-2 text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={() => appendKeyFeature({ value: "" })}
              className="flex items-center text-blue-600 hover:underline mt-2"
            >
              <FaPlus className="mr-2" />
              Add Feature
            </button>
          </div>

          {/* Image Upload Section */}
          <div className="col-span-1 md:col-span-2">
            <label className="block mb-3 text-sm font-bold text-gray-700">
              Images <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                existingImages.length +
                  selectedImages.length -
                  imagesToDelete.length ===
                0
                  ? "border-red-500 focus:ring-red-400"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">
                  Existing Images
                </h4>
                <div className="flex flex-wrap gap-3">
                  {existingImages.map((image, idx) => (
                    <div
                      key={`existing-${idx}`}
                      className="relative w-24 h-24 border rounded-lg overflow-hidden shadow-sm"
                    >
                      <img
                        src={image}
                        alt={`existing-${idx}`}
                        className="object-cover w-full h-full"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingImage(idx)}
                        className="absolute top-1 right-1 bg-white p-1 rounded-full shadow hover:bg-red-100"
                        title="Remove"
                      >
                        <FaTrash className="text-red-600 text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Images */}
            {selectedImages.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">New Images</h4>
                <div className="flex flex-wrap gap-3">
                  {selectedImages.map((image, idx) => (
                    <div
                      key={`new-${idx}`}
                      className="relative w-24 h-24 border rounded-lg overflow-hidden shadow-sm"
                    >
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`new-${idx}`}
                        className="object-cover w-full h-full"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(idx)}
                        className="absolute top-1 right-1 bg-white p-1 rounded-full shadow hover:bg-red-100"
                        title="Remove"
                      >
                        <FaTrash className="text-red-600 text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deleted Images (can be restored) */}
            {imagesToDelete.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">
                  Deleted Images (Click to restore)
                </h4>
                <div className="flex flex-wrap gap-3">
                  {imagesToDelete.map((image, idx) => (
                    <div
                      key={`deleted-${idx}`}
                      className="relative w-24 h-24 border rounded-lg overflow-hidden shadow-sm opacity-50 cursor-pointer"
                      onClick={() => handleRestoreImage(idx)}
                    >
                      <img
                        src={image}
                        alt={`deleted-${idx}`}
                        className="object-cover w-full h-full"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <span className="text-white text-xs font-bold">
                          Click to restore
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Distance & Locality Fields */}
          <div className="col-span-1 md:col-span-2">
            <label className="block text-lg font-semibold text-gray-800 mb-2">
              Distance and Locality <span className="text-red-500">*</span>
            </label>
            {distanceFields.map((item, index) => (
              <div
                key={item.id}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 relative"
              >
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Locality
                  </label>
                  <input
                    placeholder="e.g. Metro station"
                    {...register(`distances.${index}.locality`, {
                      required: "Locality is required",
                    })}
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors?.distances?.[index]?.locality
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />
                  {errors?.distances?.[index]?.locality && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.distances[index].locality.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Distance From
                  </label>
                  <input
                    placeholder="e.g. 5km"
                    {...register(`distances.${index}.distanceFrom`, {
                      required: "Distance is required",
                    })}
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors?.distances?.[index]?.distanceFrom
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />
                  {errors?.distances?.[index]?.distanceFrom && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.distances[index].distanceFrom.message}
                    </p>
                  )}
                </div>

                {distanceFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="absolute right-3 top-2 text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={() => append({ locality: "", distanceFrom: "" })}
              className="flex items-center text-blue-600 hover:underline mt-2"
            >
              <FaPlus className="mr-2" />
              Add More
            </button>
          </div>
        </form>
        {/* Submit Button */}
        <div className="px-6 pb-6">
          {" "}
          {/* Add padding to match the form */}
          <div className="flex justify-between gap-4 w-full">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 bg-gray-300 hover:bg-gray-400 text-gray-800 text-sm sm:text-base font-semibold py-3 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
            >
              {loading && <Loader />}
              {loading ? "Updating Plot..." : "Update Plot"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EditPlotModal;
